import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Lock,
  Shield,
  Upload,
  Download,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  Table,
  Loader2,
  AlertTriangle,
  Key,
  Copy,
  Check,
  X,
  Pencil,
  Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecureLoginsFolder = ({ projectId, getAuthHeaders, canAccess = false }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [editContent, setEditContent] = useState('');
  const [contentVisible, setContentVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const fetchFiles = async () => {
    if (!canAccess) return;
    
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/secure-logins/${projectId}/files`,
        getAuthHeaders()
      );
      setFiles(res.data || []);
    } catch (error) {
      console.error('Failed to load secure files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId && canAccess) {
      fetchFiles();
    }
  }, [projectId, canAccess]);

  const ALLOWED_EXTS = ['.txt', '.xlsx', '.xls', '.csv'];
  const isSpreadsheet = (name) => ['.xlsx', '.xls', '.csv'].some(e => name.toLowerCase().endsWith(e));
  const isAllowed = (name) => ALLOWED_EXTS.some(e => name.toLowerCase().endsWith(e));

  const handleFileUpload = async (e) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles || uploadFiles.length === 0) return;

    setUploading(true);
    let uploadedCount = 0;

    for (const file of uploadFiles) {
      if (!isAllowed(file.name)) {
        toast.error(`${file.name} - Only .txt, .xlsx, .xls, .csv files allowed`);
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(
          `${API_URL}/secure-logins/${projectId}/upload`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        uploadedCount++;
      } catch (error) {
        toast.error(`Failed to encrypt ${file.name}`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} file(s) encrypted & uploaded`);
      fetchFiles();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const allowedFiles = Array.from(droppedFiles).filter(f => isAllowed(f.name));
    
    if (allowedFiles.length === 0) {
      toast.error('Only .txt, .xlsx, .xls, .csv files are allowed in secure logins');
      return;
    }

    setUploading(true);
    let uploadedCount = 0;

    for (const file of allowedFiles) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(
          `${API_URL}/secure-logins/${projectId}/upload`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        uploadedCount++;
      } catch (error) {
        toast.error(`Failed to encrypt ${file.name}`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} file(s) encrypted & uploaded`);
      fetchFiles();
    }
    setUploading(false);
  };

  const handleViewFile = async (filename) => {
    try {
      const res = await axios.get(
        `${API_URL}/secure-logins/${projectId}/view/${filename}`,
        getAuthHeaders()
      );
      setViewingFile(filename);
      setFileContent(res.data.content);
      setContentVisible(false);
    } catch (error) {
      toast.error('Failed to decrypt file');
    }
  };

  const handleEditFile = async (filename) => {
    try {
      const res = await axios.get(
        `${API_URL}/secure-logins/${projectId}/view/${filename}`,
        getAuthHeaders()
      );
      setEditingFile(filename);
      setEditContent(res.data.content);
    } catch (error) {
      toast.error('Failed to load file for editing');
    }
  };

  const handleSaveFile = async () => {
    if (!editingFile) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/secure-logins/${projectId}/update/${editingFile}`,
        { content: editContent },
        getAuthHeaders()
      );
      toast.success('File saved & encrypted');
      setEditingFile(null);
      setEditContent('');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const res = await axios.get(
        `${API_URL}/secure-logins/${projectId}/download/${filename}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}? This cannot be undone.`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/secure-logins/${projectId}/delete/${filename}`,
        getAuthHeaders()
      );
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(fileContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  if (!canAccess) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-12 text-center">
          <Lock size={48} className="mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-amber-800">Restricted Access</h3>
          <p className="text-amber-600 mt-2">
            Secure logins folder is only accessible to administrators.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card 
        className={`border-slate-200 shadow-sm relative transition-all duration-200 ${isDragging ? 'ring-2 ring-green-500 ring-offset-2 border-green-400' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-green-50/90 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-green-400 pointer-events-none">
            <Shield size={48} className="text-green-500 mb-3" />
            <p className="text-lg font-semibold text-green-700">Drop files to encrypt</p>
            <p className="text-sm text-green-500 mt-1">.txt · .xlsx · .xls · .csv — AES-256 encrypted</p>
          </div>
        )}

        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                <Lock size={20} className="text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  Secure Logins
                  <Shield size={16} className="text-green-500" />
                </CardTitle>
                <p className="text-xs text-slate-500">AES-256 encrypted • Admin only</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700"
              >
                {uploading ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <Upload size={14} className="mr-1" />
                )}
                Upload File
              </Button>
            </div>
          </div>
          
          {/* Security notice */}
          <div className="flex items-start gap-2 mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Key size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-green-700">
              <strong>Encrypted Storage:</strong> Files are AES-256 encrypted before upload.
              Supports .txt, .xlsx, .xls, .csv — Drag & drop or click upload.
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Lock size={48} className="mx-auto mb-3 opacity-30" />
              <p>No encrypted files yet</p>
              <p className="text-sm mt-1">Drop .txt or spreadsheet files here to encrypt and store</p>
            </div>
          ) : (
            <div className="space-y-2">
              {files.map((file) => (
                <div
                  key={file.path}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isSpreadsheet(file.name) ? 'bg-emerald-100' : 'bg-green-100'}`}>
                      {isSpreadsheet(file.name)
                        ? <Table size={18} className="text-emerald-600" />
                        : <FileText size={18} className="text-green-600" />
                      }
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 flex items-center gap-2">
                        {file.name}
                        <Lock size={12} className="text-green-500" />
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(file.last_modified).toLocaleDateString()} • Encrypted
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!isSpreadsheet(file.name) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleViewFile(file.name)}
                        title="View decrypted content"
                      >
                        <Eye size={16} />
                      </Button>
                    )}
                    {!isSpreadsheet(file.name) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-blue-500 hover:text-blue-600"
                        onClick={() => handleEditFile(file.name)}
                        title="Edit file"
                      >
                        <Pencil size={16} />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => handleDownload(file.name)}
                      title="Download decrypted"
                    >
                      <Download size={16} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(file.name)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit File Dialog */}
      <Dialog open={!!editingFile} onOpenChange={() => {
        if (!saving) {
          setEditingFile(null);
          setEditContent('');
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={18} className="text-blue-500" />
              Edit: {editingFile}
              <span className="text-xs font-normal text-slate-500 bg-blue-100 px-2 py-0.5 rounded">
                Changes will be encrypted
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 space-y-4">
            {/* Warning banner */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                Editing sensitive credentials. Changes are auto-encrypted when saved.
              </p>
            </div>

            {/* Editor */}
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[400px] font-mono text-sm bg-slate-900 text-green-400 border-slate-700 focus:border-green-500"
              placeholder="Enter login credentials..."
            />

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-slate-500">
                {editContent.length} characters
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingFile(null);
                    setEditContent('');
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveFile}
                  disabled={saving || !editContent.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Encrypting...
                    </>
                  ) : (
                    <>
                      <Save size={14} className="mr-2" />
                      Save & Encrypt
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View File Dialog */}
      <Dialog open={!!viewingFile} onOpenChange={() => {
        setViewingFile(null);
        setFileContent('');
        setContentVisible(false);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={18} className="text-green-500" />
              {viewingFile}
              <span className="text-xs font-normal text-slate-500 bg-green-100 px-2 py-0.5 rounded">
                Decrypted
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Warning banner */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                This content is sensitive. Make sure no one is looking at your screen.
              </p>
            </div>

            {/* Content visibility toggle */}
            <div className="flex items-center justify-between mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setContentVisible(!contentVisible)}
              >
                {contentVisible ? (
                  <>
                    <EyeOff size={14} className="mr-1" />
                    Hide Content
                  </>
                ) : (
                  <>
                    <Eye size={14} className="mr-1" />
                    Show Content
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
              >
                {copied ? (
                  <>
                    <Check size={14} className="mr-1 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={14} className="mr-1" />
                    Copy All
                  </>
                )}
              </Button>
            </div>

            {/* Content display */}
            <div className="relative">
              <pre className={`p-4 bg-slate-900 text-green-400 rounded-lg overflow-auto max-h-96 text-sm font-mono ${!contentVisible ? 'blur-md select-none' : ''}`}>
                {fileContent || 'No content'}
              </pre>
              {!contentVisible && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Button
                    variant="secondary"
                    onClick={() => setContentVisible(true)}
                  >
                    <Eye size={16} className="mr-2" />
                    Click to Reveal
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SecureLoginsFolder;
